import { useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../../../contexts/AppContext";
import { Colour, UserType } from "../../Variables";
import { Card } from "../Shared/Card";
import { ErrorsDisplay } from "../Shared/Error";
import { useNavigate, useSearchParams } from "react-router";
import { CardDivider } from "../Shared/CardDivider";
import { RequiredHelper } from "../Shared/RequiredHelper";
import { LoadingContent } from "../Shared/LoadingContent";
import { Loading } from "../Shared/Loading";
import { API_GetDetails, API_GetNationalities, API_UpdateDetails, NationalityOptionModel, PersonalDetailsContainerModel, PersonalDetailsModel } from "../../../apis/ApisPersonalDetails";
import { ValidatedStringFormInput } from "./ValidatedStringFormInput";
import toast from "react-hot-toast";
import { NationalityDropdown } from "./NationalityDropdown";

export function PersonalDetailsForm() {
    const app = useContext(AppContext);
    const [errors, setErrors] = useState<string[]>([]);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [creatingApplication, setCreatingApplication] = useState<boolean>(false);
    const [personalDetails, setPersonalDetails] = useState<PersonalDetailsContainerModel>(null);
    const [details, setDetails] = useState<PersonalDetailsModel>(null);
    const [nationalities, setNationalities] = useState<NationalityOptionModel[]>([]);
    const [nationalitiesLoading, setNationalitiesLoading] = useState<boolean>(false);
    const [loadingExtra, setLoadingExtra] = useState<boolean>(true);
    const statusAlertRef = useRef<HTMLDivElement>(null);
    let navigate = useNavigate();
    const [queryParameters] = useSearchParams();
    let aref = queryParameters.get("aref");
    let a = queryParameters.get("a");
    let id = queryParameters.get("id");
    let s = queryParameters.get("s"); // is speculative
    let lmid = queryParameters.get("lmid");

    const scrollToStatusAlert = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
        statusAlertRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    useEffect(() => {
        async function asyncCall() {
            try {
                var data = await API_GetDetails(aref, id, lmid);
                setDetails(data.Details);
                setPersonalDetails(data);
            } finally {
                setLoadingExtra(false);
            }
        }

        if (!app.loading && loadingExtra) {
            asyncCall();
        }
    }, [app.loading]);

    useEffect(() => {
        if (!personalDetails?.Features?.PersonalDetails_NationalityAdvancedDropdown || !personalDetails?.Features?.PersonalDetails_Nationality) {
            return;
        }

        let cancelled = false;
        setNationalitiesLoading(true);
        API_GetNationalities()
            .then(data => {
                if (!cancelled) {
                    setNationalities(Array.isArray(data) ? data : []);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setNationalities([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setNationalitiesLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [personalDetails?.Features?.PersonalDetails_NationalityAdvancedDropdown, personalDetails?.Features?.PersonalDetails_Nationality]);

    useEffect(() => {
        if (!details || nationalities.length === 0) {
            return;
        }
        if (details.NationalityID || !(details.Nationality ?? "").length) {
            return;
        }

        const value = details.Nationality.toLowerCase();
        const match = nationalities.find(n =>
            (n.ShortName ?? "").toLowerCase() === value
            || (n.Name ?? "").toLowerCase() === value
        );
        if (match) {
            updater(match.ID.toString(), "Nationality");
        }
    }, [nationalities]);

    function updater(state: string, action: string) {
        switch (action) {
            case 'Title': {
                setDetails({ ...details, Title: state });
                break;
            } case 'Forenames': {
                setDetails({ ...details, Forenames: state });
                return;
            } case 'Initials': {
                setDetails({ ...details, Initials: state });
                return;
            } case 'Surname': {
                setDetails({ ...details, Surname: state });
                return;
            } case 'Pronouns': {
                setDetails({ ...details, Pronouns: state });
                return;
            } case 'KnownAs': {
                setDetails({ ...details, KnownAs: state });
                return;
            } case 'EmailAddress': {
                setDetails({ ...details, EmailAddress: state });
                return;
            } case 'AlternativeEmail': {
                setDetails({ ...details, AlternativeEmail: state });
                return;
            } case 'Mobile': {
                setDetails({ ...details, Mobile: state });
                return;
            } case 'HomeTel': {
                setDetails({ ...details, HomeTel: state });
                return;
            } case 'Nationality': {
                if (personalDetails?.Features?.PersonalDetails_NationalityAdvancedDropdown) {
                    const nationalityId = state ? parseInt(state, 10) : null;
                    const match = nationalityId != null && !Number.isNaN(nationalityId)
                        ? nationalities.find(n => n.ID === nationalityId)
                        : null;
                    setDetails({
                        ...details,
                        NationalityID: match?.ID ?? null,
                        Nationality: match?.ShortName ?? null,
                    });
                } else {
                    setDetails({ ...details, Nationality: state });
                }
                return;
            } case 'NINumber': {
                setDetails({ ...details, NINumber: state });
                return;
            } case 'HomeAddress': {
                setDetails({ ...details, HomeAddress: state });
                return;
            } case 'Address1': {
                setDetails({ ...details, Address1: state });
                return;
            } case 'Address2': {
                setDetails({ ...details, Address2: state });
                return;
            } case 'Address3': {
                setDetails({ ...details, Address3: state });
                return;
            } case 'City': {
                setDetails({ ...details, City: state });
                return;
            } case 'Postcode': {
                setDetails({ ...details, Postcode: state });
                return;
            } case 'HomeCountry': {
                setDetails({ ...details, HomeCountry: state });
                return;
            } case 'TermAddress': {
                setDetails({ ...details, TermAddress: state });
                return;
            } case 'TermCountry': {
                setDetails({ ...details, TermCountry: state });
                return;
            } case 'TermTel': {
                setDetails({ ...details, TermTel: state });
                return;
            } case 'TermUntilMonth': {
                setDetails({ ...details, TermUntilMonth: state });
                return;
            } case 'TermUntilYear': {
                setDetails({ ...details, TermUntilYear: state });
                return;
            } case 'PreferredName': {
                setDetails({ ...details, PreferredName: state });
                return;
            } case 'NameInChinese': {
                setDetails({ ...details, NameInChinese: state });
                return;
            }

        }
    }

    const validate = () => {
        let errors: string[] = [];
        if (personalDetails.Features.PersonalDetails_Title && personalDetails.Features.PersonalDetails_Required_Title && (details.Title ?? "").length == 0) {
            errors = ([...errors, "Please provide a title"]);
        }
        if ((details.Forenames ?? "").length == 0) {
            errors = ([...errors, "Please provide a first name"]);
        }
        if ((details.Surname ?? "").length == 0) {
            errors = ([...errors, "Please provide a last name"]);
        }
        if (personalDetails.Features.PersonalDetails_Initials && personalDetails.Features.PersonalDetails_Required_Initials && (details.Initials ?? "").length == 0) {
            errors = ([...errors, `Please provide ${personalDetails.Features.PersonalDetails_InitialsLabel.toLowerCase()}`]);
        }
        if (personalDetails.Features.PersonalDetails_KnownAs && personalDetails.Features.PersonalDetails_Required_KnownAs && (details.KnownAs ?? "").length == 0) {
            errors = ([...errors, `Please provide known as`]);
        }
        if (personalDetails.Features.PersonalDetails_Pronouns && personalDetails.Features.PersonalDetails_Required_Pronouns && (details.Pronouns ?? "").length == 0) {
            errors = ([...errors, `Please provide your pronouns`]);
        }
        if (personalDetails.Features.PersonalDetails_NINumber && personalDetails.Features.PersonalDetails_Required_NINumber && (details.NINumber ?? "").length == 0) {
            errors = ([...errors, `Please provide your NI number`]);
        }
        if (personalDetails.Features.PersonalDetails_Nationality && personalDetails.Features.PersonalDetails_Required_Nationality) {
            if (personalDetails.Features.PersonalDetails_NationalityAdvancedDropdown) {
                if (!details.NationalityID) {
                    errors = ([...errors, `Please provide your nationality`]);
                }
            } else if ((details.Nationality ?? "").length == 0) {
                errors = ([...errors, `Please provide your nationality`]);
            }
        }
        if (personalDetails.Features.PersonalDetails_Mobile && personalDetails.Features.PersonalDetails_Required_Mobile && (details.Mobile ?? "").length == 0) {
            errors = ([...errors, `Please provide your mobile number`]);
        }
        if (personalDetails.Features.PersonalDetails_HomeAddressSplit && personalDetails.Features.PersonalDetails_Required_Address1 && (details.Address1 ?? "").length == 0) {
            errors = ([...errors, `Please provide your address`]);
        }
        if (personalDetails.Features.PersonalDetails_HomeAddressSplit && personalDetails.Features.PersonalDetails_Required_City && (details.City ?? "").length == 0) {
            errors = ([...errors, `Please provide your city`]);
        }
        if (personalDetails.Features.PersonalDetails_HomeAddressSplit && personalDetails.Features.PersonalDetails_Required_Postcode && (details.Postcode ?? "").length == 0) {
            errors = ([...errors, `Please provide your postcode`]);
        }
        if (personalDetails.Features.PersonalDetails_HomeTel && personalDetails.Features.PersonalDetails_Required_HomeTel && (details.HomeTel ?? "").length == 0) {
            errors = ([...errors, `Please provide your home telephone number`]);
        }
        if (personalDetails.Features.PersonalDetails_Country && personalDetails.Features.PersonalDetails_Required_HomeCountry && (details.HomeCountry ?? "").length == 0) {
            errors = ([...errors, `Please provide a country`]);
        }
        if (personalDetails.Features.PersonalDetails_PreferredName && personalDetails.Features.PersonalDetails_Required_PreferredName && (details.PreferredName ?? "").length == 0) {
            errors = ([...errors, `Please provide a preferred name`]);
        }
        if (personalDetails.Features.PersonalDetails_NameInChinese && personalDetails.Features.PersonalDetails_Required_NameInChinese && (details.NameInChinese ?? "").length == 0) {
            errors = ([...errors, `Please provide your name in chinese`]);
        }

        if (app.loggedInUser.UserType != UserType.Agency || (app.loggedInUser.UserType != UserType.Agency && personalDetails.Features.ShowAgencyConsultantMessage))
            if ((details.EmailAddress ?? "").length == 0) {
                errors = ([...errors, "Please provide an email address"]);
            } else if (!details.EmailAddress?.includes("@")) {
                errors = ([...errors, "Please provide a valid email address"]);
            }

        setErrors(errors);
        return errors.length == 0;
    }

    const isCreatingCandidate = app.loggedInUser != null
        && app.loggedInUser.UserType != UserType.Candidate
        && app.loggedInUser.UserType != UserType.Anonymous
        && (aref?.length ?? 0) == 0;

    const saveDetails = async () => {
        function delay(ms: number) {
            return new Promise(resolve => setTimeout(resolve, ms));
        }

        try {
            setSubmitting(true);
            let isValid = validate();
            await delay(100);
            if (!isValid) {
                scrollToStatusAlert();
                toast.error("Validation issues");
                setSubmitting(false);
                return;
            }

            if (isCreatingCandidate) {
                setCreatingApplication(true);
                scrollToStatusAlert();
            }

            var data = await API_UpdateDetails(details, aref, a, id, lmid);
            if (data.Errors != null && data.Errors.length > 0) {
                setCreatingApplication(false);
                setErrors(data.Errors);
                scrollToStatusAlert();
                toast.error("Validation issues");
            }
            else if (!app.loggedInUser.PersonalDetailsFilledIn) {
                setCreatingApplication(false);
                await app.verifyUser(false);
                if ((id?.length ?? 0) > 0) {
                    if ((s?.length ?? 0) > 0) {
                        if (s == "s") {
                            navigate(`/Speculative?id=${id}`);
                        }
                        else if (s == "i") {
                            navigate(`/Intranet/PositionDetails?id=${id}${(app.token?.length ?? 0) > 0 ? ("&token=" + app.token) : ""}`);
                        }
                    }
                    else {
                        navigate(`/PositionDetails?id=${id}${(app.token?.length ?? 0) > 0 ? ("&token=" + app.token) : ""}`);
                    }
                }
                else {
                    navigate("/Home");
                }
            }
            else {
                setCreatingApplication(false);
                toast.success("Updated");
            }
            setSubmitting(false);
        }
        catch {
            setCreatingApplication(false);
            setSubmitting(false);
            toast.error("Error");
        }
    }

    let DateOptions: any = [];
    for (let i: number = new Date().getFullYear(); i < new Date().getFullYear() + 50; i++) {
        DateOptions = [...DateOptions, <option value={i} key={i}>{i}</option>]
    }

    return (
        <>
            <div ref={statusAlertRef}>
                {creatingApplication && <div className="alert alert-success"><p className="text-center"><b>Creating new application</b></p><Loading classes={"text-center"} /></div>}
            </div>
            <Card headerClasses="mb-3" headerTheme={Colour.Secondary} title="Personal details">
                <LoadingContent loading={loadingExtra || app.loading} classes={"text-center"}>
                    <form>
                        {app.userLoggedIn && app.loggedInUser.UserType == UserType.Agency && personalDetails?.Features?.ShowAgencyConsultantMessage && <div className="alert alert-info">
                            Please provide candidate details on this page, there is an opportunity to attach consultant details on the next page.
                        </div>}
                        {app.userLoggedIn && app.loggedInUser.UserType == UserType.Admin && personalDetails?.Features?.Admin_SendConsentConfirmationEmail && <div className="alert alert-info">
                            We send an automated email to candidates to confirm that we are processing and storing their data and to share information about our privacy policy.
                        </div>}
                        {app.userLoggedIn && app.loggedInUser.UserType == UserType.Agency && personalDetails?.Features?.Agency_SendConsentConfirmationEmail && <div className="alert alert-info">
                            We send an automated email to candidates to confirm that we are processing and storing their data and to share information about our privacy policy.
                        </div>}
                        <ErrorsDisplay errors={errors} />
                        <div className="mb-3">
                            <CardDivider title="Personal information" icon={"fa-solid fa-address-card"} />
                            <div className="row">
                                {details != null && personalDetails.Features.PersonalDetails_Title && !personalDetails.Features.PersonalDetails_Required_Title &&
                                    inputer(`Title ${personalDetails.Features.PersonalDetails_TitleExamples}`, details.Title, submitting, 4, 30, function (val: string): void {
                                        updater(val, "Title");
                                    })}
                                {details != null && personalDetails.Features.PersonalDetails_Title && personalDetails.Features.PersonalDetails_Required_Title &&
                                    <ValidatedStringFormInput Name={`Title ${personalDetails.Features.PersonalDetails_TitleExamples}`} BaseValue={details.Title} Invalid={(details.Title ?? "").length == 0} MaxSize={30} ColWidth={4} submitting={submitting} setValueallback={function (val: string): void {
                                        updater(val, 'Title');
                                    }} />}
                                {details != null &&
                                    <ValidatedStringFormInput Name={"First name"} BaseValue={details.Forenames} Invalid={(details.Forenames ?? "").length == 0} ColWidth={4} MaxSize={100} submitting={submitting} setValueallback={function (val: string): void {
                                        updater(val, "Forenames");
                                    }} />}
                                {details != null && personalDetails.Features.PersonalDetails_Initials && !personalDetails.Features.PersonalDetails_Required_Initials &&
                                    inputer(`${personalDetails.Features.PersonalDetails_InitialsLabel}`, details.Initials, submitting, 4, 50, function (val: string): void {
                                        updater(val, "Initials");
                                    })}
                                {details != null && personalDetails.Features.PersonalDetails_Initials && personalDetails.Features.PersonalDetails_Required_Initials &&
                                    <ValidatedStringFormInput Name={`${personalDetails.Features.PersonalDetails_InitialsLabel}`} BaseValue={details.Initials} Invalid={(details.Initials ?? "").length == 0} ColWidth={4} MaxSize={50} submitting={submitting} setValueallback={function (val: string): void {
                                        updater(val, "Initials");
                                    }} />}
                                {details != null &&
                                    <ValidatedStringFormInput Name={"Last name"} BaseValue={details.Surname} Invalid={(details.Surname ?? "").length == 0} ColWidth={4} MaxSize={100} submitting={submitting} setValueallback={function (val: string): void {
                                        updater(val, "Surname");
                                    }} />}
                                {details != null && personalDetails.Features.PersonalDetails_KnownAs && !personalDetails.Features.PersonalDetails_Required_KnownAs &&
                                    inputer("Known as", details.KnownAs, submitting, 4, 50, function (val: string): void {
                                        updater(val, "KnownAs");
                                    })}
                                {details != null && personalDetails.Features.PersonalDetails_KnownAs && personalDetails.Features.PersonalDetails_Required_KnownAs &&
                                    <ValidatedStringFormInput Name={"Known as"} BaseValue={details.KnownAs} Invalid={(details.KnownAs ?? "").length == 0} ColWidth={4} MaxSize={50} submitting={submitting} setValueallback={function (val: string): void {
                                        updater(val, "KnownAs");
                                    }} />}
                                {details != null && personalDetails.Features.PersonalDetails_Pronouns && !personalDetails.Features.PersonalDetails_Required_Pronouns &&
                                    inputer(`${personalDetails.Features.PersonalDetails_Label_User_Pronouns}`, details.Pronouns, submitting, 4, 50, function (val: string): void {
                                        updater(val, "Pronouns");
                                    })}
                                {details != null && personalDetails.Features.PersonalDetails_Pronouns && personalDetails.Features.PersonalDetails_Required_Pronouns &&
                                    <ValidatedStringFormInput Name={`${personalDetails.Features.PersonalDetails_Label_User_Pronouns}`} BaseValue={details.Pronouns} Invalid={(details.Pronouns ?? "").length == 0} ColWidth={4} MaxSize={50} submitting={submitting} setValueallback={function (val: string): void {
                                        updater(val, "Pronouns");
                                    }} />}
                                {details != null && personalDetails.Features.PersonalDetails_NINumber  && !personalDetails.Features.PersonalDetails_Required_NINumber &&
                                    inputer("NI Number", details.NINumber, submitting, 4, 20, function (val: string): void {
                                        updater(val, "NINumber");
                                    })}
                                {details != null && personalDetails.Features.PersonalDetails_NINumber && personalDetails.Features.PersonalDetails_Required_NINumber &&
                                    <ValidatedStringFormInput Name={"NI Number"} BaseValue={details.NINumber} Invalid={(details.NINumber ?? "").length == 0} ColWidth={4} MaxSize={20} submitting={submitting} setValueallback={function (val: string): void {
                                        updater(val, "NINumber");
                                    }} />}
                                {details != null && personalDetails.Features.PersonalDetails_Nationality && personalDetails.Features.PersonalDetails_NationalityAdvancedDropdown &&
                                    <NationalityDropdown
                                        nationalities={nationalities}
                                        selectedId={details.NationalityID ?? null}
                                        nationalityText={details.Nationality}
                                        required={personalDetails.Features.PersonalDetails_Required_Nationality}
                                        submitting={submitting}
                                        loading={nationalitiesLoading}
                                        onChange={(id) => {
                                            updater(id != null ? id.toString() : "", "Nationality");
                                        }}
                                    />}
                                {details != null && personalDetails.Features.PersonalDetails_Nationality && !personalDetails.Features.PersonalDetails_NationalityAdvancedDropdown && !personalDetails.Features.PersonalDetails_Required_Nationality &&
                                    inputer("Nationality", details.Nationality ?? "", submitting, 4, 30, function (val: string): void {
                                        updater(val, "Nationality");
                                    })}
                                {details != null && personalDetails.Features.PersonalDetails_Nationality && !personalDetails.Features.PersonalDetails_NationalityAdvancedDropdown && personalDetails.Features.PersonalDetails_Required_Nationality &&
                                    <ValidatedStringFormInput Name={"Nationality"} BaseValue={details.Nationality ?? ""} Invalid={(details.Nationality ?? "").length == 0} ColWidth={4} MaxSize={30} submitting={submitting} setValueallback={function (val: string): void {
                                        updater(val, "Nationality");
                                    }} />}
                                {details != null && personalDetails.Features.PersonalDetails_NameInChinese && !personalDetails.Features.PersonalDetails_Required_NameInChinese &&
                                    inputer(personalDetails.Features.PersonalDetails_NameInChinese_Label, details.NameInChinese, submitting, 4, 100, function (val: string): void {
                                        updater(val, "NameInChinese");
                                    })}
                                {details != null && personalDetails.Features.PersonalDetails_NameInChinese && personalDetails.Features.PersonalDetails_Required_NameInChinese &&
                                    <ValidatedStringFormInput Name={"Name in chinese"} BaseValue={details.NameInChinese} Invalid={(details.NameInChinese ?? "").length == 0} ColWidth={4} MaxSize={100} submitting={submitting} setValueallback={function (val: string): void {
                                        updater(val, "NameInChinese");
                                    }} />}
                                {details != null && personalDetails.Features.PersonalDetails_PreferredName && !personalDetails.Features.PersonalDetails_Required_PreferredName &&
                                    inputer("Preferred name", details.PreferredName, submitting, 4, 100, function (val: string): void {
                                        updater(val, "PreferredName");
                                    })}
                                {details != null && personalDetails.Features.PersonalDetails_PreferredName && personalDetails.Features.PersonalDetails_Required_PreferredName &&
                                    <ValidatedStringFormInput Name={"Preferred name"} BaseValue={details.PreferredName} Invalid={(details.PreferredName ?? "").length == 0} ColWidth={4} MaxSize={100} submitting={submitting} setValueallback={function (val: string): void {
                                        updater(val, "PreferredName");
                                    }} />}
                            </div>
                        </div>
                        <div className="mb-3">
                            <CardDivider title="Contact information" icon={"fa-solid fa-circle-info"} />
                            <div className="row">
                                {details != null && (app.loggedInUser.UserType != UserType.Agency || (app.loggedInUser.UserType == UserType.Agency && !personalDetails?.Features?.ShowAgencyConsultantMessage)) &&
                                    <ValidatedStringFormInput Name={"Email address"} BaseValue={details.EmailAddress} Invalid={(details.EmailAddress ?? "").length == 0} MaxSize={100} ColWidth={12} submitting={submitting} setValueallback={function (val: string): void {
                                        updater(val, "EmailAddress");
                                    }} />}

                                {details != null && (app.loggedInUser.UserType == UserType.Agency && personalDetails?.Features?.ShowAgencyConsultantMessage) &&
                                    inputer("Email address", details.EmailAddress, submitting, 12, 100, function (val: string): void {
                                        updater(val, "EmailAddress");
                                    })
                                }
                                {details != null && (app.loggedInUser.UserType == UserType.Agency || app.loggedInUser.UserType == UserType.Admin) &&
                                    <small className="text-muted">This must be the candidates email address</small>
                                }

                                {details != null && personalDetails.Features.PersonalDetails_AlternativeEmail && app.loggedInUser.UserType == UserType.Candidate &&
                                    <>
                                        {inputer("Alternative email", details.AlternativeEmail, submitting, 12, 100, function (val: string): void {
                                            updater(val, "AlternativeEmail");
                                        })}
                                        <div className="col-lg-12"><small className="text-muted no-highlight">
                                            You will be contacted in the first instance at the email
                                            address you have registered with. The alternative email
                                            address will only be used if the registration email
                                            address expires. You should be confident that this
                                            address will still be current by the end of the application
                                            procedure.</small></div></>}

                                {details != null && personalDetails.Features.PersonalDetails_HomeTel && !personalDetails.Features.PersonalDetails_Required_HomeTel &&
                                    inputer("Home telephone", details.HomeTel, submitting, 6, 30, function (val: string): void {
                                        updater(val, "HomeTel");
                                    })
                                }
                                {details != null && personalDetails.Features.PersonalDetails_HomeTel && personalDetails.Features.PersonalDetails_Required_HomeTel &&
                                    <ValidatedStringFormInput Name={"Home telephone"} BaseValue={details.HomeTel} Invalid={(details.HomeTel ?? "").length == 0} ColWidth={6} MaxSize={30} submitting={submitting} setValueallback={function (val: string): void {
                                        updater(val, "HomeTel");
                                    }} />}
                                {details != null && personalDetails.Features.PersonalDetails_Mobile && !personalDetails.Features.PersonalDetails_Required_Mobile &&
                                    inputer("Mobile", details.Mobile, submitting, 6, 30, function (val: string): void {
                                        updater(val, "Mobile");
                                    })
                                }
                                {details != null && personalDetails.Features.PersonalDetails_Mobile && personalDetails.Features.PersonalDetails_Required_Mobile &&
                                    <ValidatedStringFormInput Name={"Mobile"} BaseValue={details.Mobile} Invalid={(details.Mobile ?? "").length == 0} ColWidth={6} MaxSize={30} submitting={submitting} setValueallback={function (val: string): void {
                                        updater(val, "Mobile");
                                    }} />}
                            </div>
                        </div>
                        <div className="mb-3">
                            {details != null && personalDetails.Features.PersonalDetails_Show_AddressTitle && 
                                <CardDivider title="Address" icon={"fa-solid fa-house-user"} />
                            }
                            <div className="row">
                                {details != null && personalDetails.Features.PersonalDetails_HomeAddress && <div className={`col-lg-12`} key={`InputControl_TermAddress`}>
                                    <label htmlFor={`control_HomeAddress`} className="form-label-alt">Address</label>
                                    <textarea
                                        className="form-control"
                                        rows={4}
                                        value={details.HomeAddress ?? ''}
                                        maxLength={1000}
                                        disabled={submitting}
                                        onChange={t => {
                                            updater(t.currentTarget.value, "HomeAddress");
                                        }}></textarea>
                                </div>}
                                {details != null && personalDetails.Features.PersonalDetails_HomeAddressSplit && !personalDetails.Features.PersonalDetails_Required_Address1 &&
                                    inputer("Address line 1", details.Address1, submitting, 12, 100, function (val: string): void {
                                        updater(val, "Address1");
                                    })}
                                {details != null && personalDetails.Features.PersonalDetails_HomeAddressSplit && personalDetails.Features.PersonalDetails_Required_Address1 &&
                                    <ValidatedStringFormInput Name={"Address line 1"} BaseValue={details.Address1} Invalid={(details.Address1 ?? "").length == 0} ColWidth={6} MaxSize={30} submitting={submitting} setValueallback={function (val: string): void {
                                        updater(val, "Address1");
                                    }} />
                                }
                                {details != null && personalDetails.Features.PersonalDetails_HomeAddressSplit &&
                                    inputer("Address line 2", details.Address2, submitting, 6, 100, function (val: string): void {
                                        updater(val, "Address2");
                                    })}
                                {details != null && personalDetails.Features.PersonalDetails_HomeAddressSplit &&
                                    inputer("Address line 3", details.Address3, submitting, 6, 100, function (val: string): void {
                                        updater(val, "Address3");
                                    })}
                                {details != null && personalDetails.Features.PersonalDetails_City && !personalDetails.Features.PersonalDetails_Required_City &&
                                    inputer("City", details.City, submitting, 6, 100, function (val: string): void {
                                        updater(val, "City");
                                    })}
                                {details != null && personalDetails.Features.PersonalDetails_City && personalDetails.Features.PersonalDetails_Required_City &&
                                    <ValidatedStringFormInput Name={"City"} BaseValue={details.City} Invalid={(details.City ?? "").length == 0} ColWidth={6} MaxSize={30} submitting={submitting} setValueallback={function (val: string): void {
                                        updater(val, "City");
                                    }} />
                                }
                                {details != null && personalDetails.Features.PersonalDetails_Postcode && !personalDetails.Features.PersonalDetails_Required_Postcode &&
                                    inputer("Postcode", details.Postcode, submitting, 6, 100, function (val: string): void {
                                        updater(val, "Postcode");
                                    })}
                                {details != null && personalDetails.Features.PersonalDetails_Postcode && personalDetails.Features.PersonalDetails_Required_Postcode &&
                                    <ValidatedStringFormInput Name={"Postcode"} BaseValue={details.Postcode} Invalid={(details.Postcode ?? "").length == 0} ColWidth={6} MaxSize={30} submitting={submitting} setValueallback={function (val: string): void {
                                        updater(val, "Postcode");
                                    }} />
                                }
                                {details != null && personalDetails.Features.PersonalDetails_Country && !personalDetails.Features.PersonalDetails_Required_HomeCountry &&
                                    inputer("Country", details.HomeCountry, submitting, 6, 30, function (val: string): void {
                                        updater(val, "HomeCountry");
                                    })}
                                {details != null && personalDetails.Features.PersonalDetails_Country && personalDetails.Features.PersonalDetails_Required_HomeCountry &&
                                    <ValidatedStringFormInput Name={"Country"} BaseValue={details.HomeCountry} Invalid={(details.HomeCountry ?? "").length == 0} ColWidth={6} MaxSize={30} submitting={submitting} setValueallback={function (val: string): void {
                                        updater(val, "HomeCountry");
                                    }} />
                                }
                            </div>
                        </div>

                        {!loadingExtra && personalDetails.Features.PersonalDetails_TermAddress &&
                            <div className="mb-3">
                                <CardDivider title="Term address" icon={"fa-solid fa-house-user"} />
                                <div className="row">

                                    <div className={`col-lg-12`} key={`InputControl_TermAddress`}>
                                        <label htmlFor={`control_TermAddress`} className="form-label-alt">Term address</label>
                                        <textarea
                                            className="form-control"
                                            rows={4}
                                            disabled={submitting}
                                            value={details.TermAddress ?? ''}
                                            maxLength={1000}
                                            onChange={t => {
                                                updater(t.currentTarget.value, "TermAddress");
                                            }}></textarea>
                                    </div>
                                    {inputer("TermCountry", details.TermCountry, submitting, 6, 30, function (val: string): void {
                                        updater(val, "TermCountry");
                                    })}
                                    {inputer("TermTel", details.TermTel, submitting, 6, 30, function (val: string): void {
                                        updater(val, "TermTel");
                                    })}
                                    <div className={`col-lg-12`} key={`InputControl_TermUntil`}>
                                        <label htmlFor="useUntilControls" className="form-label-alt">Use address until</label>
                                        <div className="input-group">
                                            <select disabled={submitting} onChange={x => { x.preventDefault(); updater(x.currentTarget.value, "TermUntilMonth"); }} value={details.TermUntilMonth} defaultValue={""} className="form-select" aria-label="Use address until month">
                                                <option value={""} key={"Please_select"}>Please select</option>
                                                <option value={"1"} key={"Jan"}>Jan</option>
                                                <option value={"2"} key={"Feb"}>Feb</option>
                                                <option value={"3"} key={"Mar"}>Mar</option>
                                                <option value={"4"} key={"Apr"}>Apr</option>
                                                <option value={"5"} key={"May"}>May</option>
                                                <option value={"6"} key={"Jun"}>Jun</option>
                                                <option value={"7"} key={"Jul"}>Jul</option>
                                                <option value={"8"} key={"Aug"}>Aug</option>
                                                <option value={"9"} key={"Sep"}>Sep</option>
                                                <option value={"10"} key={"Oct"}>Oct</option>
                                                <option value={"11"} key={"Nov"}>Nov</option>
                                                <option value={"12"} key={"Dec"}>Dec</option>
                                            </select>

                                            <select disabled={submitting} onChange={x => { x.preventDefault(); updater(x.currentTarget.value, "TermUntilYear"); }} value={details.TermUntilYear} defaultValue={""} className="form-select" aria-label="Use address until month">
                                                <option value={""} key={"Please_select"}>Please select</option>
                                                {DateOptions}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        }
                        <div className="row">
                            <div className="col-lg-12 text-center">
                                <button className="btn btn-alt mt-4 btn-wider" disabled={submitting || loadingExtra} type="submit" onClick={e => {
                                    e.preventDefault();
                                    saveDetails();
                                }}><i className="fa-solid fa-floppy-disk"></i> {app.loggedInUser.UserType != UserType.Candidate && app.loggedInUser.UserType != UserType.Anonymous && (aref?.length ?? 0) == 0 && <span>Create and apply</span>}{app.loggedInUser.UserType != UserType.Candidate && app.loggedInUser.UserType != UserType.Anonymous && (aref?.length ?? 0) > 0 && <span>Save</span>}{app.loggedInUser.UserType == UserType.Candidate && <span>Save</span>}</button>
                            </div>
                        </div>
                    </form>
                </LoadingContent>
            </Card >
            <RequiredHelper />
        </>
    );
}

export type ValidatedStringFormInputProps = {
    Name: string,
    BaseValue: string,
    Invalid: boolean,

    ColWidth: number,
    submitting: boolean,
    setValueallback: (val: string) => void,
}

const inputer = (Name: string, BaseValue: string, submitting: boolean, ColWidth: number, MaxSize: number, setValueallback: (val: string) => void) => {

    return (
        <div className={`col-lg-${ColWidth}`} key={`InputControl_${Name}`}>
            <label htmlFor={`control_${Name}`} className="form-label-alt">{Name}</label>
            <input disabled={submitting} id={`control_${Name}`} aria-label={Name} className="form-control"
                value={BaseValue ?? ''}
                type="text"
                maxLength={MaxSize}
                onChange={t => {
                    setValueallback(t.currentTarget.value);
                }} />
        </div>);
}